import React, { useEffect, useMemo, useState } from "react";
import { useScheduleData } from "./ScheduleDataContext";

// Helpers to robustly extract ids and labels from varying API shapes
function extractStringDeep(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "object") {
    // common nested keys
    const keys = ["value", "name", "label", "display", "text", "title"];
    for (const k of keys) {
      if (typeof v[k] === "string") return v[k];
    }
    // fallback to first string value in object
    for (const val of Object.values(v)) {
      if (typeof val === "string") return val;
    }
  }
  return "";
}

function getRoleId(role) {
  const candidates = [
    role?.id,
    role?.workRoleId,
    role?.identifier,
    role?.connectWiseId,
    role?.value?.id,
    typeof role?.value === "number" || typeof role?.value === "string" ? role.value : undefined,
  ];
  let rid = candidates.find((x) => x !== undefined && x !== null);
  if (typeof rid === "string" && /^\d+$/.test(rid)) rid = Number(rid);
  return rid;
}

function getRoleLabel(role) {
  const name = extractStringDeep(role?.name);
  const label =
    name ||
    extractStringDeep(role?.description) ||
    extractStringDeep(role?.workRoleName) ||
    extractStringDeep(role?.workRole) ||
    (getRoleId(role) != null ? String(getRoleId(role)) : "(unnamed role)");
  return label;
}

function getUserLabel(u) {
  return (
    extractStringDeep(u?.name) ||
    extractStringDeep(u?.fullName) ||
    extractStringDeep(u?.identifier) ||
    ""
  );
}

function useSearch(list, keyFn, storageKey) {
  const [q, setQ] = useState(() => {
    try {
      return (storageKey && localStorage.getItem(storageKey)) || "";
    } catch {
      return "";
    }
  });
  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, q);
    } catch {}
  }, [q, storageKey]);
  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list || [];
    return (list || []).filter(it => (keyFn(it) || '').toLowerCase().includes(needle));
  }, [list, q, keyFn]);
  return { q, setQ, items };
}

export default function WorkRoleUserFilters() {
  const {
    workRoles,
    workRolesLoading,
    workRolesError,
    selectedWorkRoleIds,
    setSelectedWorkRoleIds,
    usersForRoles,
    usersLoading,
    selectedUserIds,
    setSelectedUserIds,
    refresh,
  } = useScheduleData();

  // Local staged selection for debouncing commits to context
  const [stagedRoleIds, setStagedRoleIds] = useState(() => selectedWorkRoleIds);
  const DEBOUNCE_MS = 1000; // wait time after last click
  // If committed roles change externally (to a non-empty set), reflect that in staged
  useEffect(() => {
    if (!Array.isArray(selectedWorkRoleIds)) return;
    if (selectedWorkRoleIds.length === 0) return; // avoid wiping staged during local edits
    const a = [...selectedWorkRoleIds].sort();
    const b = [...stagedRoleIds].sort();
    const same = a.length === b.length && a.every((v, i) => v === b[i]);
    if (!same) setStagedRoleIds(selectedWorkRoleIds);
  }, [selectedWorkRoleIds]);
  useEffect(() => {
    const handle = setTimeout(() => {
      // Only commit if different
      const sortedA = [...selectedWorkRoleIds].sort();
      const sortedB = [...stagedRoleIds].sort();
      const same = sortedA.length === sortedB.length && sortedA.every((v,i) => v === sortedB[i]);
      if (!same) setSelectedWorkRoleIds(stagedRoleIds);
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [stagedRoleIds, setSelectedWorkRoleIds, selectedWorkRoleIds]);

  const { q: roleQ, setQ: setRoleQ, items: roleItems } = useSearch(workRoles, (r) => getRoleLabel(r), 'rm.roleQuery');
  const { q: userQ, setQ: setUserQ, items: userItems } = useSearch(usersForRoles, (u) => getUserLabel(u), 'rm.userQuery');

  // Use staged role ids for UI immediate feedback
  const selectedRoleSet = useMemo(() => new Set(stagedRoleIds), [stagedRoleIds]);
  const selectedUserSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);

  function toggleRole(id) {
    if (id === undefined || id === null) return;
    setStagedRoleIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return Array.from(s);
    });
  }

  function getUserId(u) {
    const candidates = [
      u?.id,
      u?.identifier,
      u?.member?.identifier,
      u?.memberId,
      u?.userId,
    ];
    let uid = candidates.find((x) => x !== undefined && x !== null);
    if (typeof uid === 'string' && /^\d+$/.test(uid)) uid = Number(uid);
    return uid ?? null;
  }

  function toggleUserById(id) {
    if (id === undefined || id === null) return;
    setSelectedUserIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return Array.from(s);
    });
  }

  return (
    <div style={{
      border: '1px solid #e0e0e0',
      borderRadius: 6,
      padding: 8,
      marginBottom: 8,
      background: 'var(--blue_accent, #1f4ca1)',
      color: 'var(--white_coordinating, #ffffff)'
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <strong style={{ fontSize:14 }}>Role & User Filters</strong>
        <span style={{ fontSize:11, opacity:0.75 }}>Roles: {workRoles.length} | Users: {usersForRoles.length}</span>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 260 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 4 }}>
            <div style={{ fontWeight: 600 }}>Work Roles</div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => {
                // Select all currently displayed roles
                const allIds = roleItems.map(r => getRoleId(r)).filter(id => id !== undefined && id !== null);
                setStagedRoleIds(allIds);
                setSelectedWorkRoleIds(allIds);
              }} style={{ background: '#ffffff', color: '#000000', border: '1px solid #ccc', borderRadius: 4, padding: '4px 8px' }}>Select all</button>
              <button onClick={() => { setStagedRoleIds([]); setSelectedWorkRoleIds([]); }} style={{ background: '#ffffff', color: '#000000', border: '1px solid #ccc', borderRadius: 4, padding: '4px 8px' }}>Clear roles</button>
            </div>
          </div>
          <input
            placeholder="Search roles..."
            value={roleQ}
            onChange={(e) => setRoleQ(e.target.value)}
            style={{ width: '100%', padding: '6px 8px', marginBottom: 6, background: '#ffffff', color: '#000000', border: '1px solid #ccc', borderRadius: 4 }}
          />
          <div style={{ maxHeight: 180, overflow: 'auto', border: '1px solid #ccc', background: '#ffffff', color: '#000000' }}>
            {workRolesLoading && <div style={{ padding: 8 }}>Loading roles...</div>}
            {!workRolesLoading && workRolesError && (
              <div style={{ padding: 8, color: '#b00020' }}>
                Failed to load roles: {workRolesError.message}
                <div><button onClick={refresh} style={{ marginTop: 6 }}>Retry</button></div>
              </div>
            )}
            {!workRolesLoading && !workRolesError && roleItems.length === 0 && (
              <div style={{ padding: 8 }}>No roles found</div>
            )}
            {!workRolesLoading && !workRolesError && roleItems.map(r => {
              const rid = getRoleId(r);
              const rlabel = getRoleLabel(r);
              const key = rid ?? rlabel ?? Math.random();
              return (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', color: '#000000' }}>
                  <input type="checkbox" checked={rid != null && selectedRoleSet.has(rid)} onChange={() => toggleRole(rid)} />
                  <span style={{ color: '#000000' }}>{rlabel}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div style={{ minWidth: 300 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 4 }}>
            <div style={{ fontWeight: 600 }}>Users</div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => {
                // Select all currently displayed users
                const allUserIds = userItems.map(u => getUserId(u)).filter(id => id !== undefined && id !== null);
                setSelectedUserIds(allUserIds);
              }} style={{ background: '#ffffff', color: '#000000', border: '1px solid #ccc', borderRadius: 4, padding: '4px 8px' }}>Select all</button>
              <button onClick={() => setSelectedUserIds([])} style={{ background: '#ffffff', color: '#000000', border: '1px solid #ccc', borderRadius: 4, padding: '4px 8px' }}>Clear users</button>
            </div>
          </div>
          <input
            placeholder="Search users..."
            value={userQ}
            onChange={(e) => setUserQ(e.target.value)}
            style={{ width: '100%', padding: '6px 8px', marginBottom: 6, background: '#ffffff', color: '#000000', border: '1px solid #ccc', borderRadius: 4 }}
          />
          <div style={{ maxHeight: 180, overflow: 'auto', border: '1px solid #ccc', background: '#ffffff', color: '#000000' }}>
            {usersLoading && <div style={{ padding: 8 }}>Loading users...</div>}
            {!usersLoading && selectedWorkRoleIds.length > 0 && userItems.length === 0 && (
              <div style={{ padding: 8 }}>No users for selected roles</div>
            )}
            {!usersLoading && userItems.map(u => {
              const name = getUserLabel(u);
              const uid = getUserId(u);
              const key = uid ?? name;
              return (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', color: '#000000' }}>
                  <input type="checkbox" checked={uid != null && selectedUserSet.has(uid)} onChange={() => toggleUserById(uid)} />
                  <span style={{ color: '#000000' }}>{name}</span>
                </label>
              );
            })}
          </div>
        </div>
        
      </div>
    </div>
  );
}
